import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, makeStyles, Tooltip, Typography } from '@material-ui/core';
import useRootSelector from 'common/hooks/useRootSelector';
import { ActionItem, ActionsMenu, DialogComponent, Table } from 'common/components';
import { IHeadCell, ITableParams, ITableRow, IBodyCell } from 'common/interfaces/table';
import commonMessages from 'common/messages/messages';
import tableHeaders from 'common/messages/tableHeaders';
import messages from 'modules/reports/messages/messages';
import personProfileMessages from 'common/components/PersonProfile/messages';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { FilterTypes, IFilterSettings } from 'common/interfaces/filter';
import tableFilters from 'common/messages/tableFilters';
import { makeTableParams } from 'common/utils/http';
import { TableOrderByParams } from 'common/constants';
import { useAppDispatch } from 'store/hooks';
import {
  fetchInventoryRedeemHistory,
  resetInventoryRedeemHistory,
} from 'common/components/PersonProfile/state/servicesModals/actions';
import * as selectors from 'common/components/PersonProfile/state/servicesModals/selectors';
import { PeakModules } from 'common/constants/peakModules';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import UnredeemSessionsModal from 'modules/reports/components/UnredeemSessionsModal/UnredeemSessionsModal';
import useUnredeemService from '../../hooks/useUnredeemService';
import { enqueueErrorNotification } from 'common/state/notifications/actions';
import PersonSimpleView from 'common/components/PersonSimpleView/PersonSimpleView';
import { makeEmployeeLabel } from 'utils/employee.utils';
import { getString } from 'common/utils/typeUtils';
import { CustomTheme } from 'common/ui/interfaces';
import { InfoIcon } from 'img/icons';
import EmployeeLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/EmployeeLinkWithAvatar';
import MemberLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/MemberLinkWithAvatar';

const useStyles = makeStyles((theme: CustomTheme) => ({
  content: {
    color: theme.palette.primary.contrastText,
  },
  infoIcon: {
    color: theme.palette.darkBackground?.light,
    width: '1.05rem',
    height: '1.05rem',
    marginLeft: theme.spacing(0.75),
    '&:hover': {
      color: theme.palette.darkBackground?.main,
    },
  },
  personView: {
    width: 'fit-content',
    marginLeft: theme.spacing(0.5),
  },
}));

interface IRedeemingHistoryModalProps {
  module: PeakModules;
  isOpen: boolean;
  profileId: number;
  onClose: () => void;
}

const RedeemingInventoryHistoryModal = (props: IRedeemingHistoryModalProps): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { isOpen, onClose, profileId, module } = props;
  const { MEMBER_SESSION_UNREDEEM } = usePermissionContext();
  const [isUnredeemModalOpen, setIsUnredeemModalOpen] = useState(false);
  const [currentRedemptionId, setCurrentRedemptionId] = useState<number | null>(null);
  const unredeemService = useUnredeemService(module);
  const inventoryRedeemHistory = useRootSelector(selectors.selectInventoryRedeemHistory);
  const inventoryRedeemHistoryLoading = useRootSelector(
    selectors.selectInventoryRedeemHistoryLoading,
  );
  const selectedInventoryInstanceId = useRootSelector(selectors.selectSelectedInventoryId);

  const redeemHistoryMeta = inventoryRedeemHistory?.get('meta');
  const redeemHistory = inventoryRedeemHistory?.get('data');

  const tableFilterSettings: IFilterSettings[] = useMemo(
    () => [
      {
        name: '',
        title: <FormattedMessage {...tableFilters.period} />,
        type: FilterTypes.DATE_RANGE,
        options: {
          startDate: null,
          endDate: null,
        },
      },
    ],
    [],
  );

  const [tableParams, setTableParams] = useState(() =>
    makeTableParams(tableFilterSettings, null, {
      orderBy: TableOrderByParams.DATE,
      order: 'desc',
    }),
  );

  const [timezoneMoment] = useTimezoneMoment();

  useEffect(() => {
    dispatch(
      fetchInventoryRedeemHistory(profileId, selectedInventoryInstanceId, tableParams, module),
    );
  }, [tableParams, dispatch, profileId, selectedInventoryInstanceId, module]);

  useEffect(() => {
    return () => {
      dispatch(resetInventoryRedeemHistory());
    };
  }, [dispatch]);

  const headerOptions: IHeadCell[] = [
    { id: 'amount', label: intl.formatMessage(commonMessages.items), sort: true },
    { id: 'member', label: intl.formatMessage(tableHeaders.member), sort: true },
    { id: 'redeemed', label: intl.formatMessage(tableHeaders.redeemed), sort: false },
    { id: 'date', label: intl.formatMessage(tableHeaders.date), sort: true },
    {
      id: TableOrderByParams.UNREDEEMED,
      label: intl.formatMessage(tableHeaders.status),
      sort: true,
    },
  ];

  if (MEMBER_SESSION_UNREDEEM) {
    headerOptions.push({ id: 'actions', label: '', sort: false });
  }

  const tableRows: ITableRow[] = useMemo(
    () =>
      redeemHistory
        ?.map(redeemItem => {
          const unredeemedBy = redeemItem.get('unredeemedBy');
          const unredeemed = redeemItem.get('unredeemed');

          const bodyCells: IBodyCell[] = [
            {
              label: redeemItem.get('amount'),
            },
            {
              label: '',
              cellComponent: (
                <MemberLinkWithAvatar
                  id={redeemItem.getIn(['customer', 'id'])}
                  firstName={redeemItem.getIn(['customer', 'firstName'])}
                  lastName={redeemItem.getIn(['customer', 'lastName'])}
                  imageUrl={redeemItem.getIn(['customer', 'imageUrl'])}
                />
              ),
            },
            {
              label: '',
              cellComponent: (
                <EmployeeLinkWithAvatar employee={redeemItem.get('employee').toJS()} />
              ),
            },
            {
              label: `${timezoneMoment(redeemItem.get('date')).format(DEFAULT_DATE_TIME_FORMAT)}`,
            },
            {
              label: '',
              className: 'body-cell',
              cellComponent: (
                <Box display="flex">
                  <Typography component="span">
                    <FormattedMessage
                      {...(unredeemed ? commonMessages.unredeemed : commonMessages.redeemed)}
                    />
                  </Typography>

                  {unredeemed && (
                    <>
                      &nbsp;
                      <Tooltip
                        title={
                          <Box className={classes.content} display="flex" flexDirection="column">
                            <Typography color="inherit" component="span">
                              <FormattedMessage
                                {...messages.unredeemedDate}
                                values={{
                                  date: timezoneMoment(redeemItem.get('unredeemedDate')).format(
                                    DEFAULT_DATE_TIME_FORMAT,
                                  ),
                                }}
                              />
                            </Typography>
                            <Box display="flex" alignItems="center">
                              <Typography color="inherit" noWrap>
                                <FormattedMessage {...messages.unredeemedBy} />:
                              </Typography>
                              &nbsp;
                              <PersonSimpleView
                                bold
                                className={classes.personView}
                                titleColor="inherit"
                                firstName={getString(
                                  redeemItem.getIn(['unredeemedBy', 'firstName']),
                                )}
                                lastName={getString(redeemItem.getIn(['unredeemedBy', 'lastName']))}
                                imageUrl={redeemItem.getIn(['unredeemedBy', 'imageUrl'])}
                                title={
                                  unredeemedBy ? makeEmployeeLabel(unredeemedBy.toJS()) : undefined
                                }
                              />
                            </Box>
                          </Box>
                        }
                      >
                        <InfoIcon color="inherit" className={classes.infoIcon} fontSize="small" />
                      </Tooltip>
                    </>
                  )}
                </Box>
              ),
            },
          ];

          if (MEMBER_SESSION_UNREDEEM) {
            bodyCells.push({
              label: '',
              className: 'body-actions-cell',
              cellComponent: (
                <ActionsMenu horizontal tableActionsMode>
                  <ActionItem
                    onClick={() => {
                      setIsUnredeemModalOpen(true);
                      setCurrentRedemptionId(redeemItem.get('id'));
                    }}
                    disabled={redeemItem.get('unredeemed')}
                  >
                    <FormattedMessage {...commonMessages.unredeemBtn} />
                  </ActionItem>
                </ActionsMenu>
              ),
            });
          }

          return {
            id: redeemItem.get('id'),
            cells: bodyCells,
          };
        })
        .toJS() || [],
    [redeemHistory, timezoneMoment, MEMBER_SESSION_UNREDEEM, classes],
  );

  const onChangeTableParams = useCallback((tableProps: ITableParams): void => {
    setTableParams(tableProps);
  }, []);

  const onUnredeemModalClose = () => {
    setIsUnredeemModalOpen(false);
  };

  const onSubmit = data => {
    if (unredeemService) {
      unredeemService({
        data: { ...data, isService: false },
        personId: profileId,
        redemptionId: currentRedemptionId,
      })
        .unwrap()
        .then(() => {
          setIsUnredeemModalOpen(false);
          dispatch(
            fetchInventoryRedeemHistory(
              profileId,
              selectedInventoryInstanceId,
              tableParams,
              module,
            ),
          );
        })
        .catch(e => dispatch(enqueueErrorNotification(e)));
    }
  };

  return (
    <DialogComponent
      isOpen={isOpen}
      onClose={onClose}
      title={intl.formatMessage(personProfileMessages.redeemHistoryTitle)}
      hideFooterButtons
      scroll="paper"
      supressBottomShadow
      size="xl"
    >
      <>
        <Table
          rows={tableRows}
          headerOptions={headerOptions}
          showPerPageSelect
          isLoading={inventoryRedeemHistoryLoading}
          page={redeemHistoryMeta?.get('page')}
          totalRows={redeemHistoryMeta?.get('total')}
          onChangeParams={onChangeTableParams}
          filters={tableFilterSettings}
          tableParams={tableParams}
        />
        {isUnredeemModalOpen && (
          <UnredeemSessionsModal
            disabled={false}
            isBulkUnredeem={false}
            onSubmit={onSubmit}
            onClose={onUnredeemModalClose}
            isOpen
          />
        )}
      </>
    </DialogComponent>
  );
};

export default RedeemingInventoryHistoryModal;
